import React from 'react';
import './terms.style.css';
import chipsImage from "../../assets/compressed/logoWildChip-min.png"
import SEO from '../../SEO';

const Terms: React.FC = () => {
    // Define safe zones for chips (avoiding text content)
    const chipPositions = Array.from({ length: 40 }, () => ({
        top: `${Math.floor(Math.random() * 2000)}px`,
        left: `${Math.random() < 0.5 
            ? Math.floor(Math.random() * 20) // 0-20%
            : Math.floor(Math.random() * 40) + 80}%`, // 60-100%
        rotation: `${Math.random() * 360}deg`,
        opacity: 0.5 + (Math.random() * 0.5) // Random between 0.5 and 0.8
    }));

    return (
        <>
        <SEO
            title="Terms of Service - Wild Chips Poker"
            description="Review Wild Chips Poker's terms of service. Understand the rules, guidelines, and conditions for playing our jungle-themed poker game."
            keywords="wild chips poker terms of service, poker game terms, terms and conditions, poker game rules, legal terms"
            ogImage="/images/bgAbout-min.png"
            canonicalUrl="https://wildchipspoker.com/terms"
        />
        <div className="all-pages-padding terms-container-bg">
            <div className="random-chips-container">
                {chipPositions.map((position, index) => (
                    <img 
                        key={index}
                        src={chipsImage} 
                        alt="Chips" 
                        style={{ 
                            position: 'absolute',
                            ...position,
                            transform: `rotate(${position.rotation})`,
                            opacity: position.opacity
                        }} 
                    />
                ))}
            </div>
            <div className="terms-container">
                <h1>Poker Game Terms</h1>

                <section>
                    <h2>Basic Terms</h2>
                    <ul>
                        <li><strong>Ante:</strong> A small bet all players must make before the hand begins.</li>
                        <li><strong>Blind:</strong> Forced bets made before the cards are dealt.</li>
                        <li><strong>Small Blind:</strong> The first forced bet, typically half the minimum bet.</li>
                        <li><strong>Big Blind:</strong> The second forced bet, typically equal to the minimum bet.</li>
                        <li><strong>Dealer Button:</strong> A marker indicating the theoretical dealer position.</li>
                    </ul>
                </section>
                <section>
                    <h2>Betting Terms</h2>
                    <ul>
                        <li><strong>Call:</strong> To match the current bet.</li>
                        <li><strong>Raise:</strong> To increase the current bet.</li>
                        <li><strong>Fold:</strong> To discard your hand and exit the current round.</li>
                        <li><strong>Check:</strong> To pass the action to the next player without betting.</li>
                        <li><strong>All-in:</strong> To bet all of your remaining chips.</li>
                    </ul>
                </section>

                <section>
                    <h2>Hand Rankings</h2>
                    <ul>
                        <li><strong>Royal Flush:</strong> A, K, Q, J, 10 of the same suit.</li>
                        <li><strong>Straight Flush:</strong> Five consecutive cards of the same suit.</li>
                        <li><strong>Four of a Kind:</strong> Four cards of the same rank.</li>
                        <li><strong>Full House:</strong> Three of a kind plus a pair.</li>
                        <li><strong>Flush:</strong> Five cards of the same suit.</li>
                        <li><strong>Straight:</strong> Five consecutive cards of any suit.</li>
                        <li><strong>Three of a Kind:</strong> Three cards of the same rank.</li>
                        <li><strong>Two Pair:</strong> Two different pairs.</li>
                        <li><strong>One Pair:</strong> Two cards of the same rank.</li>
                        <li><strong>High Card:</strong> The highest card when no other hand is made.</li>
                    </ul>
                </section>

                <section>
                    <h2>Game Terms</h2>
                    <ul>
                        <li><strong>Pot:</strong> The total amount of chips bet in a hand.</li>
                        <li><strong>Showdown:</strong> The final phase where remaining players reveal their cards.</li>
                        <li><strong>Muck:</strong> To discard your hand without showing it.</li>
                        <li><strong>Side Pot:</strong> A separate pot created when a player goes all-in.</li>
                        <li><strong>Position:</strong> A player's location relative to the dealer button.</li>
                    </ul>
                </section>

                <section>
                    <h2>Table Terms</h2>
                    <ul>
                        <li><strong>Button:</strong> The dealer position.</li>
                        <li><strong>Cutoff:</strong> The position to the right of the button.</li>
                        <li><strong>Hijack:</strong> The position to the right of the cutoff.</li>
                        <li><strong>Under the Gun:</strong> The first position to act after the blinds.</li>
                        <li><strong>Middle Position:</strong> The positions between early and late position.</li>
                    </ul>
                </section>

                <section>
                    <h2>Legal Requirements</h2>
                    <ul>
                        <li><strong>Age Requirement:</strong> Must be 18 years or older to play.</li>
                        <li><strong>Account Limit:</strong> Only one account per person is allowed.</li>
                        <li><strong>Verification:</strong> Account verification is required before full access.</li>
                        <li><strong>Personal Use:</strong> Must be playing on your own behalf, not for others.</li>
                    </ul>
                </section>

                {/* <section>
        <h2>Responsible Gaming</h2>
        <ul>
          <li><strong>Deposit Limits:</strong> Can set daily, weekly, and monthly deposit limits.</li>
          <li><strong>Timeout Options:</strong> Available for 1 day to 3 months.</li>
          <li><strong>Self-Exclusion:</strong> Available for 6 months, 1 year, or 5 years.</li>
        </ul>
      </section> */}

                {/* <section>
        <h2>Financial Terms</h2>
        <ul>
          <li><strong>Minimum Withdrawal:</strong> CAD$10 (except for final account closure).</li>
          <li><strong>Withdrawal Fee:</strong> $1 fee applies to withdrawals.</li>
          <li><strong>Processing Time:</strong> 24-72 hours for withdrawals to process.</li>
          <li><strong>Fund Protection:</strong> Player funds held in segregated accounts.</li>
        </ul>
      </section> */}

                <section>
                    <h2>Prohibited Activities</h2>
                    <ul>
                        <li><strong>No Cheating:</strong> Cheating or collusion is strictly prohibited.</li>
                        <li><strong>No Bots:</strong> Use of bots or automated systems is not allowed.</li>
                        <li><strong>Security:</strong> No circumventing security measures.</li>
                        <li><strong>Access:</strong> No unauthorized access attempts permitted.</li>
                    </ul>
                </section>

                <section>
                    <h2>Technical & Support</h2>
                    <ul>
                        <li><strong>Server Issues:</strong> Games may be cancelled during server crashes.</li>
                        <li><strong>Disconnections:</strong> Specific procedures for handling disconnected games.</li>
                        <li><strong>Support:</strong> Contact help@wildchipspoker.com for assistance.</li>
                    </ul>
                </section>
            </div>
        </div>
        </>
    );
};

export default Terms;
